import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreditCard, Loader2, Plus, ShieldCheck, Star, Trash2 } from "lucide-react";

import api from "../../api/Client";
import { useSileo } from "../../components/Sileo";
import styles from "./PaymentMethodsPage.module.css";

type PaymentMethod = {
  id: string;
  provider: string;
  payer_ref: string | null;
  pmt_ref: string | null;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default?: boolean;
  created_at?: string | null;
};

type PaymentMethodsResponse = {
  configured: boolean;
  is_active: boolean;
  methods: PaymentMethod[];
  default_payment_method_id?: string | null;
};

export default function PaymentMethodsPage() {
  const sileo = useSileo();
  const location = useLocation();
  const navigate = useNavigate();

  const [configured, setConfigured] = useState(true);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadMethods();
  }, []);

  useEffect(() => {
    void handleCheckoutReturn();
  }, [location.search]);

  async function loadMethods(options?: { silent?: boolean }) {
    if (!options?.silent) {
      setLoading(true);
    }

    setError(null);

    try {
      const response = await api.get<PaymentMethodsResponse>("/api/customers/payment-methods");
      const nextMethods = Array.isArray(response.data?.methods) ? response.data.methods : [];
      const defaultId = response.data?.default_payment_method_id || null;

      setConfigured(Boolean(response.data?.configured) && Boolean(response.data?.is_active));
      setMethods(
        nextMethods.map((method) => ({
          ...method,
          is_default: method.id === defaultId || method.is_default === true
        }))
      );
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudieron cargar las tarjetas.";
      setError(message);
      if (!options?.silent) {
        sileo.error(message, { durationMs: 2400 });
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }

  async function handleCheckoutReturn() {
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get("checkout");
    const sessionId = params.get("session_id");

    if (!checkoutStatus) {
      return;
    }

    if (checkoutStatus === "cancel") {
      sileo.info("Alta de tarjeta cancelada.", { durationMs: 2000 });
      navigate("/payment-methods", { replace: true });
      return;
    }

    if (checkoutStatus === "success" && sessionId) {
      try {
        const response = await api.get<{ status?: string }>(
          `/api/customers/payment-methods/setup-session/${encodeURIComponent(sessionId)}`
        );

        if (response.data?.status === "complete") {
          await loadMethods({ silent: true });
          sileo.success("Tarjeta guardada correctamente.", { durationMs: 2200 });
        } else {
          sileo.warning("Stripe no confirmo la tarjeta todavia.", { durationMs: 2400 });
        }
      } catch (requestError: any) {
        const message = requestError?.response?.data?.error || "No se pudo verificar la tarjeta guardada.";
        sileo.error(message, { durationMs: 2400 });
      } finally {
        navigate("/payment-methods", { replace: true });
      }
    }
  }

  async function createSetupSession() {
    setCreatingSession(true);
    try {
      const returnUrl = `${window.location.origin}/payment-methods`;
      const response = await api.post<{ url?: string }>("/api/customers/payment-methods/setup-session", {
        return_url: returnUrl
      });

      const checkoutUrl = String(response.data?.url || "").trim();
      if (!checkoutUrl) {
        throw new Error("Stripe no devolvio la URL de checkout.");
      }

      window.location.href = checkoutUrl;
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudo iniciar Stripe.";
      sileo.error(message, { durationMs: 2400 });
    } finally {
      setCreatingSession(false);
    }
  }

  async function makeDefault(paymentMethodId: string) {
    setBusyId(paymentMethodId);
    try {
      await api.post("/api/customers/payment-methods/default", {
        payment_method_id: paymentMethodId
      });
      await loadMethods({ silent: true });
      sileo.success("Tarjeta predeterminada actualizada.", { durationMs: 2200 });
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudo actualizar la tarjeta predeterminada.";
      sileo.error(message, { durationMs: 2400 });
    } finally {
      setBusyId(null);
    }
  }

  async function removeMethod(paymentMethodId: string) {
    const confirmed = window.confirm("Quieres eliminar esta tarjeta?");
    if (!confirmed) {
      return;
    }

    setBusyId(paymentMethodId);
    try {
      await api.delete(`/api/customers/payment-methods/${encodeURIComponent(paymentMethodId)}`);
      setMethods((current) => current.filter((item) => item.id !== paymentMethodId));
      sileo.info("Tarjeta eliminada.", { durationMs: 2200 });
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudo eliminar la tarjeta.";
      sileo.error(message, { durationMs: 2400 });
    } finally {
      setBusyId(null);
    }
  }

  const defaultMethod = useMemo(() => methods.find((method) => method.is_default) || null, [methods]);

  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroBadge}>
          <ShieldCheck size={14} />
          Stripe seguro
        </div>
        <h1>Gestiona tus tarjetas guardadas</h1>
        <p>
          Guarda tu tarjeta para compras futuras y elige cual quieres dejar como metodo predeterminado.
        </p>
        <div className={styles.heroMeta}>
          <div className={styles.heroStat}>
            <span>Tarjetas</span>
            <strong>{methods.length}</strong>
          </div>
          <div className={styles.heroStat}>
            <span>Predeterminada</span>
            <strong>{defaultMethod ? `**** ${defaultMethod.last4 || "--"}` : "Sin definir"}</strong>
          </div>
        </div>
      </section>

      <section className={styles.card}>
        <header className={styles.cardHeader}>
          <div>
            <h2>Anadir tarjeta</h2>
            <p>Stripe abrira una pantalla segura para guardar el metodo de pago en tu perfil.</p>
          </div>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => void createSetupSession()}
            disabled={creatingSession || !configured}
          >
            {creatingSession ? <Loader2 size={16} className={styles.spin} /> : <Plus size={16} />}
            {creatingSession ? "Abriendo Stripe..." : "Nueva tarjeta"}
          </button>
        </header>

        {!configured && (
          <div className={styles.warningBox}>
            El centro todavia no ha configurado Stripe. Cuando lo active, podras guardar tarjetas aqui.
          </div>
        )}
      </section>

      <section className={styles.card}>
        <header className={styles.cardHeader}>
          <div>
            <h2>Tus metodos de pago</h2>
            <p>Puedes cambiar la predeterminada o eliminar tarjetas que ya no uses.</p>
          </div>
        </header>

        {loading && (
          <div className={styles.stateRow}>
            <Loader2 size={16} className={styles.spin} />
            <span>Cargando tarjetas...</span>
          </div>
        )}

        {!loading && error && <p className={styles.errorText}>{error}</p>}

        {!loading && !error && methods.length === 0 && (
          <div className={styles.emptyState}>
            <CreditCard size={20} />
            <strong>No tienes tarjetas guardadas</strong>
            <span>Cuando anadas una, quedara lista para futuras compras con Stripe.</span>
          </div>
        )}

        {!loading && !error && methods.length > 0 && (
          <div className={styles.methodList}>
            {methods.map((method) => {
              const isBusy = busyId === method.id;

              return (
                <article key={method.id} className={styles.methodItem}>
                  <div className={styles.cardIcon}>
                    <CreditCard size={18} />
                  </div>

                  <div className={styles.methodInfo}>
                    <strong>{formatBrand(method.brand)} ···· {method.last4 || "----"}</strong>
                    <span>{formatExpiry(method.exp_month, method.exp_year)}</span>
                  </div>

                  <div className={styles.methodActions}>
                    {method.is_default ? (
                      <span className={styles.defaultPill}>
                        <Star size={12} />
                        Predeterminada
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={styles.ghostButton}
                        onClick={() => void makeDefault(method.id)}
                        disabled={Boolean(busyId)}
                      >
                        {isBusy ? <Loader2 size={16} className={styles.spin} /> : <Star size={16} />}
                        Predeterminar
                      </button>
                    )}

                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => void removeMethod(method.id)}
                      disabled={Boolean(busyId)}
                    >
                      {isBusy ? <Loader2 size={16} className={styles.spin} /> : <Trash2 size={16} />}
                      Eliminar
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}

function formatBrand(value: string | null) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return "Tarjeta";
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function formatExpiry(month: number | null, year: number | null) {
  if (!month || !year) {
    return "Caducidad no disponible";
  }
  return `Caduca ${String(month).padStart(2, "0")}/${year}`;
}
